import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import { selectExtendedVariant, extendedMaySource } from "../../../extendedVariantSelection";

import {
  absLinearLatex,
  compareIntervalSets,
  containsAllNumbers,
  frac,
  fracLatex,
  fracRaw,
  fracValue,
  MathLine,
  rand,
  updatePart,
  userRaw,
} from "./absoluteValueHelpers";

export const type = "MaturaAbsoluteValueInequalityOpenTask";

const ORIGINAL = { p: 2, a: 2, b: 3, c: 2 };
const VALUES = Array.from({ length: 8 }, (_, i) => i + 1);
const OPTIONS = [2, 3, 4].flatMap((p) => VALUES.flatMap((a) => VALUES.flatMap((b) =>
  Array.from({ length: 10 }, (_, i) => ({ p, a, b, c: i + 1 }))
))).filter(({ p, a, b, c }) => {
  const left = -(c + a + p * b) / (p - 1);
  const right = (c + a - p * b) / (p + 1);
  return left < -b && right > -b && right < a
    && !(p === 2 && a === 2 && b === 3 && c === 2);
});

export function getVariantCount() { return OPTIONS.length; }

function makeTask(params = rand(OPTIONS)) {
  const { p, a, b, c } = params;
  const left = frac(-(c + a + p * b), p - 1);
  const right = frac(c + a - p * b, p + 1);
  return {
    type,
    points: 4,
    p,
    a,
    b,
    c,
    variantIndex: params.variantIndex,
    breakpoints: [frac(-b), frac(a)],
    left,
    right,
    answerRaw: `(-∞; ${fracRaw(left)}) ∪ (${fracRaw(right)}; ∞)`,
  };
}

export function generateTask(options = {}) {
  const original = options.variant === "original";
  const task = makeTask(original ? ORIGINAL : selectExtendedVariant(OPTIONS, options));
  return { ...task, source: extendedMaySource("5", original) };
}

export function renderTask(task) {
  return (
    <div>
      <p><strong>Zadanie maturalne - otwarte (0-4 pkt).</strong></p>
      <p>Rozwiąż nierówność</p>
      <BlockMath math={`${absLinearLatex(1, -task.a)}- ${task.p}\\cdot ${absLinearLatex(1, task.b)} < -${task.c}`} />
      <p><strong>Zapisz obliczenia.</strong></p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const set = (key) => (value) => onChange(updatePart(userAnswer, key, value));
  return (
    <div style={{ display: "grid", gap: 18 }}>
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p><strong>Krok 1 (1 pkt).</strong> Zapisz punkty podziału osi i przedziały przypadków.</p>
        <EquationInput
          value={userAnswer.cases || ""}
          onChange={set("cases")}
          disabled={disabled}
          placeholder={`np. -${task.b}, ${task.a} oraz (-∞,-${task.b}), [-${task.b},${task.a}), [${task.a},∞)`}
          showPreview={false}
        />
      </div>
      <div>
        <p><strong>Krok 2 (1 pkt).</strong> Zapisz nierówności bez wartości bezwzględnej w rozważanych przedziałach.</p>
        <EquationInput
          value={userAnswer.noAbs || ""}
          onChange={set("noAbs")}
          disabled={disabled}
          placeholder={`np. dla x<-${task.b}: -x+${task.a}-${task.p}(-x-${task.b})<-${task.c}`}
          showPreview={false}
        />
      </div>
      <div>
        <p><strong>Krok 3 (1 pkt).</strong> Rozwiąż przypadki i wskaż granice rozwiązania.</p>
        <EquationInput
          value={userAnswer.bounds || ""}
          onChange={set("bounds")}
          disabled={disabled}
          placeholder={`np. x<${fracRaw(task.left)}, x>${fracRaw(task.right)}`}
          showPreview={false}
        />
      </div>
      <div>
        <p><strong>Krok 4 (1 pkt).</strong> Podaj końcowy zbiór rozwiązań.</p>
        <IntervalInput
          value={userAnswer.final || ""}
          onChange={set("final")}
          disabled={disabled}
          placeholder={`np. (-∞; ${fracRaw(task.left)}) U (${fracRaw(task.right)}; ∞)`}
          previewWithX
        />
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer = {}) {
  const expectedIntervals = [
    { left: -Infinity, right: fracValue(task.left), leftClosed: false, rightClosed: false },
    { left: fracValue(task.right), right: Infinity, leftClosed: false, rightClosed: false },
  ];
  const steps = {
    cases: containsAllNumbers(userRaw(userAnswer.cases), task.breakpoints),
    noAbs: (() => {
      const raw = userRaw(userAnswer.noAbs);
      const s = raw.replace(/\s+/g, "");
      return raw && !raw.includes("|") && /[<>≤≥]/.test(raw) && /x/.test(s);
    })(),
    bounds: containsAllNumbers(userRaw(userAnswer.bounds), [task.left, task.right]),
    final: compareIntervalSets(parseIntervalInput(userRaw(userAnswer.final)), expectedIntervals),
  };
  const score = Object.values(steps).filter(Boolean).length;
  return { correct: score === 4, score, maxScore: 4, details: steps };
}

export function renderAnswer(task) {
  return (
    <div>
      <p><strong>Krok 1:</strong> punkty podziału: <InlineMath math={`${fracLatex(task.breakpoints[0])}`} /> oraz <InlineMath math={`${fracLatex(task.breakpoints[1])}`} />.</p>
      <p><strong>Krok 2:</strong> rozpatrujemy trzy przedziały bez symboli wartości bezwzględnej.</p>
      <p><strong>Krok 3:</strong> z przypadków otrzymujemy granice <InlineMath math={`x<${fracLatex(task.left)}`} /> oraz <InlineMath math={`x>${fracLatex(task.right)}`} />.</p>
      <p><strong>Krok 4:</strong> <InlineMath math={`x\\in ${intervalToLatex(task.answerRaw)}`} />.</p>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div>
      {userAnswer.cases && <p>Przedziały: {userRaw(userAnswer.cases)}</p>}
      {userAnswer.noAbs && <p>Bez wartości bezwzględnej: {userRaw(userAnswer.noAbs)}</p>}
      {userAnswer.bounds && <p>Granice: {userRaw(userAnswer.bounds)}</p>}
      {userAnswer.final && <p>Odpowiedź: <InlineMath math={intervalToLatex(userRaw(userAnswer.final), { withX: true })} /></p>}
    </div>
  );
}

export function renderExplanation(task) {
  const { p, a, b, c } = task;
  return (
    <div>
      <p><strong>Krok 1.</strong> Punkty, w których wyrażenia pod wartościami bezwzględnymi zmieniają znak, to:</p>
      <MathLine math={`x=-${b}\\quad\\text{oraz}\\quad x=${a}`} />
      <p>Dlatego rozpatrujemy przedziały <InlineMath math={`(-\\infty,-${b})`} />, <InlineMath math={`[-${b},${a})`} /> oraz <InlineMath math={`[${a},+\\infty)`} />.</p>
      <p><strong>Krok 2.</strong> Usuwamy wartości bezwzględne w każdym przedziale.</p>
      <MathLine math={`x<-${b}:\\quad -(x-${a})-${p}(-(x+${b}))<-${c}`} />
      <MathLine math={`-${b}\\le x<${a}:\\quad -(x-${a})-${p}(x+${b})<-${c}`} />
      <MathLine math={`x\\ge ${a}:\\quad (x-${a})-${p}(x+${b})<-${c}`} />
      <p><strong>Krok 3.</strong> Po rozwiązaniu przypadków otrzymujemy:</p>
      <MathLine math={`x<${fracLatex(task.left)}\\quad\\text{lub}\\quad x>${fracLatex(task.right)}`} />
      <p><strong>Krok 4.</strong> Zbiór rozwiązań nierówności jest równy:</p>
      <MathLine math={`x\\in ${intervalToLatex(task.answerRaw)}`} />
    </div>
  );
}
